import { useState } from 'react'
import {
  Anchor, Button, Group, NumberInput, Select, Stack, Switch, Text, TextInput, Textarea,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconPlus } from '@tabler/icons-react'
import { useForm } from '@mantine/form'
import { zod4Resolver as zodResolver } from 'mantine-form-zod-resolver'
import { useQuery } from '@tanstack/react-query'
import {
  AFECTACIONES, SERIES, TIPOS_ORDEN, formatearNumero, ordenEntrada,
  type Empresa, type Orden, type OrdenEntrada, type Proveedor, type TipoOrden,
} from '@serkat/shared'
import { api } from '../../../api/cliente'
import { aSelect, useCatalogos } from '../../../api/catalogos'
import { hoyLocal } from '../../../compartido/formato'
import { TablaItemsOrden } from './TablaItemsOrden'
import { ModalObra } from '../../obras/componentes/ModalObra'
import { ModalArea } from '../../areas/componentes/ModalArea'
import { useSesion } from '../../auth/useSesion'

interface Props {
  inicial?: Orden
  guardando: boolean
  onGuardar: (datos: OrdenEntrada) => void
  onCancelar: () => void
}

export function FormularioOrden({ inicial, guardando, onGuardar, onCancelar }: Props) {
  const { data: catalogos } = useCatalogos()

  const { data: empresas } = useQuery({
    queryKey: ['empresas', 'activas'],
    queryFn: () => api.get<Empresa[]>('/empresas'),
  })

  const { data: proveedores } = useQuery({
    queryKey: ['proveedores', 'todos'],
    queryFn: () => api.get<{ datos: Proveedor[] }>('/proveedores?porPagina=100&activo=true'),
  })

  const form = useForm<OrdenEntrada>({
    initialValues: {
      empresaId: inicial?.empresaId ?? 0,
      correlativo: inicial?.correlativo ?? 0,
      tipo: inicial?.tipo ?? 'COMPRA',
      proveedorId: inicial?.proveedorId ?? '',
      requerimientoId: inicial?.requerimientoId ?? null,
      fechaEmision: inicial?.fechaEmision ?? hoyLocal(),
      obraId: inicial?.obraId ?? null,
      areaId: inicial?.areaId ?? null,
      monedaId: inicial?.monedaId ?? 0,
      tipoCambio: inicial?.tipoCambio ?? null,
      afectacion: inicial?.afectacion ?? 'GRAVADO',
      igvPorcentaje: inicial?.igvPorcentaje ?? 0.18,
      formaPagoId: inicial?.formaPagoId ?? null,
      tiempoEntrega: inicial?.tiempoEntrega ?? null,
      lugarEntrega: inicial?.lugarEntrega ?? null,
      sujetaDetraccion: inicial?.sujetaDetraccion ?? false,
      sujetaRetencion: inicial?.sujetaRetencion ?? false,
      retencionPorcentaje: inicial?.retencionPorcentaje ?? 0.08,
      totalEnLetras: inicial?.totalEnLetras ?? null,
      observaciones: inicial?.observaciones ?? null,
      items: inicial?.items.map((i) => ({
        itemNumero: i.itemNumero, insumoId: i.insumoId, descripcion: i.descripcion,
        unidadId: i.unidadId, cantidad: i.cantidad, precioUnitario: i.precioUnitario,
      })) ?? [],
    },
    validate: zodResolver(ordenEntrada),
  })

  // El correlativo se propone SEGÚN EL TIPO: compra y servicio no comparten libro.
  const { data: propuesto } = useQuery({
    queryKey: ['ordenes', 'siguiente-numero', form.values.tipo],
    queryFn: () =>
      api.get<{ correlativo: number }>(`/ordenes/siguiente-numero?tipo=${form.values.tipo}`),
    enabled: !inicial,
    staleTime: 0,
  })

  if (!inicial) {
    if (propuesto && form.values.correlativo === 0) {
      form.setFieldValue('correlativo', propuesto.correlativo)
    }
    if (catalogos && form.values.monedaId === 0 && catalogos.monedas[0]) {
      form.setFieldValue('monedaId', catalogos.monedas[0].id)
    }
  }

  const monedaSel = catalogos?.monedas.find((m) => m.id === form.values.monedaId)
  // Alta de obra y de área sin abandonar la orden. `buscadoObra` y
  // `buscadaArea` guardan lo tecleado en cada selector: si el valor no estaba
  // en la lista, ese texto es justo su nombre, y volver a escribirlo en el
  // modal sería pedir dos veces lo mismo.
  const { usuario } = useSesion()
  const puedeCrearMaestros = usuario?.rol === 'PROCURA' || usuario?.rol === 'ADMIN'
  const [buscadoObra, setBuscadoObra] = useState('')
  const [obraNueva, modalObra] = useDisclosure(false)
  const [buscadaArea, setBuscadaArea] = useState('')
  const [areaNueva, modalArea] = useDisclosure(false)

  const esExtranjera = Boolean(monedaSel && monedaSel.codigo !== 'PEN')
  // La retención de cuarta categoría grava honorarios, y los honorarios son
  // un servicio. En una compra de bienes no aplica.
  const esServicio = form.values.tipo === 'SERVICIO'
  const numero = form.values.correlativo
    ? formatearNumero(SERIES.ORDEN, form.values.correlativo)
    : '—'

  function cambiarTipo(v: string | null) {
    const tipo = (v ?? 'COMPRA') as TipoOrden
    form.setFieldValue('tipo', tipo)
    // Cada tipo tiene su propio correlativo: el propuesto deja de servir.
    if (!inicial) form.setFieldValue('correlativo', 0)
    // Deshabilitar la casilla no basta: conservaría el valor y se guardaría
    // igual. Una compra con retención marcada llegaría a la base.
    if (tipo !== 'SERVICIO' && form.values.sujetaRetencion) cambiarRetencion(false)
  }

  // Un recibo por honorarios no está gravado con IGV: la retención de cuarta
  // categoría y el IGV no conviven. Al marcarla se fija INAFECTO —la operación
  // queda fuera del alcance del impuesto, que no es lo mismo que exonerada— y el
  // campo se bloquea. Al quitarla vuelve el GRAVADO de siempre, porque dejarlo
  // en INAFECTO arrastraría a la orden una decisión que ya no se tomó.
  function cambiarRetencion(activa: boolean) {
    form.setFieldValue('sujetaRetencion', activa)
    form.setFieldValue('afectacion', activa ? 'INAFECTO' : 'GRAVADO')
    form.setFieldValue('igvPorcentaje', activa ? 0 : 0.18)
  }

  function cambiarAfectacion(v: string | null) {
    const a = (v ?? 'GRAVADO') as OrdenEntrada['afectacion']
    form.setFieldValue('afectacion', a)
    form.setFieldValue('igvPorcentaje', a === 'GRAVADO' ? 0.18 : 0)
  }

  return (
    <>
    <form onSubmit={form.onSubmit(onGuardar)}>
      <Stack gap="sm">
        <Group grow align="flex-start">
          <Select
            label="Tipo" withAsterisk disabled={Boolean(inicial)}
            data={TIPOS_ORDEN.map((t) => ({ value: t, label: t === 'COMPRA' ? 'Orden de compra' : 'Orden de servicio' }))}
            value={form.values.tipo} onChange={cambiarTipo}
          />
          <Select
            label="Empresa emisora" withAsterisk
            placeholder="Elija la empresa que emite"
            description="Se elige en cada documento; no hay una predeterminada" 
            data={(empresas ?? []).map((e) => ({ value: String(e.id), label: e.razonSocial }))}
            value={form.values.empresaId ? String(form.values.empresaId) : null}
            onChange={(v) => form.setFieldValue('empresaId', Number(v))}
            error={form.errors['empresaId']}
          />
          <NumberInput
            label="Correlativo" description={`Número: ${numero}`} withAsterisk min={1} hideControls
            {...form.getInputProps('correlativo')}
          />
        </Group>

        <Group grow align="flex-start">
          <Select
            label="Proveedor" withAsterisk searchable
            data={(proveedores?.datos ?? []).map((p) => ({ value: p.id, label: `${p.razonSocial} — ${p.ruc}` }))}
            value={form.values.proveedorId || null}
            onChange={(v) => form.setFieldValue('proveedorId', v ?? '')}
            error={form.errors['proveedorId']}
          />
          <TextInput label="Fecha de emisión" type="date" withAsterisk {...form.getInputProps('fechaEmision')} />
        </Group>

        <Group grow align="flex-start">
          <Stack gap={4}>
            <Select label="Obra" data={aSelect(catalogos?.obras)} clearable searchable
              nothingFoundMessage="Ninguna obra con ese nombre"
              onSearchChange={(v) => { if (v.trim()) setBuscadoObra(v) }}
              value={form.values.obraId ? String(form.values.obraId) : null}
              onChange={(v) => form.setFieldValue('obraId', v ? Number(v) : null)} />
            {puedeCrearMaestros && (
              <Anchor component="button" type="button" size="xs" onClick={modalObra.open}>
                <Group gap={4} wrap="nowrap">
                  <IconPlus size={12} />
                  Agregar obra
                </Group>
              </Anchor>
            )}
          </Stack>
          <Stack gap={4}>
            <Select label="Área solicitante" data={aSelect(catalogos?.areas)} clearable searchable
              nothingFoundMessage="Ninguna área con ese nombre"
              onSearchChange={(v) => { if (v.trim()) setBuscadaArea(v) }}
              value={form.values.areaId ? String(form.values.areaId) : null}
              onChange={(v) => form.setFieldValue('areaId', v ? Number(v) : null)} />
            {puedeCrearMaestros && (
              <Anchor component="button" type="button" size="xs" onClick={modalArea.open}>
                <Group gap={4} wrap="nowrap">
                  <IconPlus size={12} />
                  Agregar área
                </Group>
              </Anchor>
            )}
          </Stack>
        </Group>

        <Group grow align="flex-start">
          <Select
            label="Moneda" withAsterisk
            data={(catalogos?.monedas ?? []).map((m) => ({ value: String(m.id), label: `${m.codigo} — ${m.nombre}` }))}
            value={form.values.monedaId ? String(form.values.monedaId) : null}
            onChange={(v) => form.setFieldValue('monedaId', Number(v))}
          />
          <NumberInput
            label="Tipo de cambio"
            description={esExtranjera ? 'Obligatorio en moneda extranjera' : 'Solo para moneda extranjera'}
            disabled={!esExtranjera} min={0.0001} decimalScale={4} hideControls
            value={form.values.tipoCambio ?? ''}
            onChange={(v) => form.setFieldValue('tipoCambio', v === '' ? null : Number(v))}
            error={form.errors['tipoCambio']}
          />
          <Select
              label="Afectación IGV"
              description={form.values.sujetaRetencion
                ? 'Los honorarios no llevan IGV'
                : 'Gravado, exonerado o inafecto'}
              disabled={form.values.sujetaRetencion}
              data={[...AFECTACIONES]} value={form.values.afectacion}
            onChange={cambiarAfectacion} />
        </Group>

        <TablaItemsOrden
          items={form.values.items}
          monedaCodigo={(monedaSel?.codigo as 'PEN' | 'USD') ?? 'PEN'}
          igvPorcentaje={form.values.igvPorcentaje}
          retencionPorcentaje={
            form.values.sujetaRetencion ? form.values.retencionPorcentaje : null
          }
          errores={form.errors as Record<string, string | undefined>}
          onCambiar={(items) => form.setFieldValue('items', items)}
        />

        <Group grow align="flex-start">
          <Select label="Forma de pago" data={aSelect(catalogos?.formasPago)} clearable
            value={form.values.formaPagoId ? String(form.values.formaPagoId) : null}
            onChange={(v) => form.setFieldValue('formaPagoId', v ? Number(v) : null)} />
          <TextInput label="Tiempo de entrega" value={form.values.tiempoEntrega ?? ''}
            onChange={(e) => form.setFieldValue('tiempoEntrega', e.currentTarget.value || null)} />
        </Group>

        <TextInput label="Lugar de entrega" value={form.values.lugarEntrega ?? ''}
          onChange={(e) => form.setFieldValue('lugarEntrega', e.currentTarget.value || null)} />

        <Switch
          label="Operación sujeta a detracción (SPOT)"
          description="Imprime la anotación y la cuenta de detracción del proveedor"
          {...form.getInputProps('sujetaDetraccion', { type: 'checkbox' })}
        />

        <Switch
          label={`Sujeta a retención (${(form.values.retencionPorcentaje * 100).toFixed(0)}%)`}
          description={esServicio
            ? 'Renta de cuarta categoría. No cambia el total: se descuenta al pagar'
            : 'Solo en órdenes de servicio: la retención grava honorarios'}
          disabled={!esServicio}
          checked={form.values.sujetaRetencion}
          onChange={(e) => cambiarRetencion(e.currentTarget.checked)}
        />

        <Textarea label="Observaciones" autosize minRows={2}
          value={form.values.observaciones ?? ''}
          onChange={(e) => form.setFieldValue('observaciones', e.currentTarget.value || null)} />

        <Group justify="space-between" mt="xs">
          <Text size="xs" c="dimmed">
            Se emite en estado Pendiente, con las cláusulas contractuales de la empresa emisora.
          </Text>
          <Group>
            <Button variant="default" onClick={onCancelar}>Cancelar</Button>
            <Button type="submit" loading={guardando}>Guardar</Button>
          </Group>
        </Group>
      </Stack>
    </form>

    <ModalObra
      abierto={obraNueva}
      onCerrar={modalObra.close}
      nombreSugerido={form.values.obraId ? '' : buscadoObra}
      onGuardada={(o) => form.setFieldValue('obraId', o.id)}
    />

    <ModalArea
      abierto={areaNueva}
      onCerrar={modalArea.close}
      nombreSugerido={form.values.areaId ? '' : buscadaArea}
      onGuardada={(a) => form.setFieldValue('areaId', a.id)}
    />
</>
  )
}
